import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Users, IndianRupee, ScanLine, AlertTriangle, CalendarClock, UserPlus, Wallet, Bell, ChevronRight, Building2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from "recharts";
import { rank } from "../../lib/rbac";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => "₹" + (n || 0).toLocaleString("en-IN");

const StatCard = ({ label, value, icon: Icon, accent, testid }) => (
  <div data-testid={testid} className="bg-card_bg border border-border_soft rounded-xl p-5 hover:border-gold/40 transition-colors">
    <div className="flex justify-between items-start mb-4">
      <h3 className="text-[11px] font-semibold text-text_secondary uppercase tracking-wider">{label}</h3>
      <div className={`p-2 rounded-lg ${accent}`}>
        <Icon size={18} strokeWidth={2} />
      </div>
    </div>
    <p className="text-3xl font-bold text-text_primary tracking-tight">{value}</p>
  </div>
);

const QuickAction = ({ to, icon: Icon, label, testid }) => (
  <Link to={to} data-testid={testid} className="flex items-center gap-3 bg-card_bg border border-border_soft rounded-xl px-5 py-4 hover:border-gold/50 hover:bg-sidebar_hover transition-all group">
    <div className="bg-gold/10 text-gold p-2.5 rounded-lg group-hover:bg-gold group-hover:text-[#111827] transition-colors"><Icon size={20} /></div>
    <span className="text-sm font-semibold text-text_primary">{label}</span>
  </Link>
);

const chartTooltip = { backgroundColor: "#131A26", border: "1px solid #243047", borderRadius: "8px", color: "#E5E7EB" };

const RevenueChart = ({ data }) => {
  const margin = useMemo(() => ({ top: 10, right: 10, left: -10, bottom: 0 }), []);
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={margin}>
        <defs>
          <linearGradient id="goldFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#FACC15" stopOpacity={0.4} />
            <stop offset="95%" stopColor="#FACC15" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#243047" vertical={false} />
        <XAxis dataKey="name" stroke="#9CA3AF" fontSize={11} tickLine={false} axisLine={false} dy={8} />
        <YAxis stroke="#9CA3AF" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${v / 1000}k`} />
        <Tooltip contentStyle={chartTooltip} formatter={(v) => fmt(v)} />
        <Area type="monotone" dataKey="revenue" stroke="#FACC15" strokeWidth={2.5} fill="url(#goldFill)" activeDot={{ r: 6, fill: "#FACC15", stroke: "#0B0F1A", strokeWidth: 2 }} />
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default function AdminDashboard() {
  const [stats, setStats] = useState({});
  const [revenueTrend, setRevenueTrend] = useState([]);
  const [attendanceTrend, setAttendanceTrend] = useState([]);
  const [packageData, setPackageData] = useState([]);
  const [alerts, setAlerts] = useState({ expiring: [], dues: [], followups: [] });
  const [user, setUser] = useState({ name: "Admin" });
  const [branches, setBranches] = useState([]);
  const [branch, setBranch] = useState("all");

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/dashboard/stats?branch=${branch}`);
      const d = res.data;
      setStats(d.stats || {});
      setRevenueTrend(d.revenue_trend || []);
      setAttendanceTrend(d.attendance_trend || []);
      setPackageData(d.package_data || []);
      setAlerts(d.alerts || { expiring: [], dues: [], followups: [] });
    } catch (e) { console.error(e); }
  }, [branch]);

  const fetchMeta = useCallback(async () => {
    try {
      const [authRes, br] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/auth/me`),
        axios.get(`${BACKEND_URL}/api/branches`),
      ]);
      if (authRes.data?.user) setUser(authRes.data.user);
      setBranches(br.data || []);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchMeta(); }, [fetchMeta]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const showBranchFilter = rank(user.role) >= 80;

  const greeting = (() => {
    const h = new Date().getHours();
    return h < 12 ? "Good Morning" : h < 17 ? "Good Afternoon" : "Good Evening";
  })();

  return (
    <div data-testid="admin-dashboard" className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-serif text-text_primary mb-1">{greeting}, {user.name}</h1>
          <p className="text-text_secondary text-sm">Here's what's happening at SVT Golden{branch !== "all" ? ` · ${branch}` : showBranchFilter ? " (all branches)" : ""} today.</p>
        </div>
        {showBranchFilter && (
          <div className="flex items-center gap-2 bg-card_bg border border-border_soft rounded-xl px-3 py-2.5">
            <Building2 size={16} className="text-gold" />
            <select data-testid="dashboard-branch-filter" value={branch} onChange={(e) => setBranch(e.target.value)} className="bg-transparent text-text_primary text-sm font-semibold outline-none cursor-pointer">
              <option value="all">All Branches</option>
              {branches.map((b) => <option key={b.id} value={b.name}>{b.name}</option>)}
            </select>
          </div>
        )}
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <QuickAction to="/admin/members/new" icon={UserPlus} label="Add Member" testid="quick-add-member" />
        <QuickAction to="/admin/attendance" icon={ScanLine} label="Check In" testid="quick-checkin" />
        <QuickAction to="/admin/invoices" icon={Wallet} label="Collect Payment" testid="quick-collect" />
        <QuickAction to="/admin/leads" icon={Users} label="Add Lead" testid="quick-add-lead" />
      </div>

      {/* Metric cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard testid="stat-active-members" label="Active Members" value={stats.active_clients || 0} icon={Users} accent="bg-accent_success/10 text-accent_success" />
        <StatCard testid="stat-revenue-today" label="Revenue Today" value={fmt(stats.revenue_today)} icon={IndianRupee} accent="bg-gold/10 text-gold" />
        <StatCard testid="stat-revenue-month" label="Revenue (Month)" value={fmt(stats.revenue_month)} icon={IndianRupee} accent="bg-gold/10 text-gold" />
        <StatCard testid="stat-pending-dues" label="Pending Dues" value={fmt(stats.pending_dues)} icon={AlertTriangle} accent="bg-accent_danger/10 text-accent_danger" />
        <StatCard testid="stat-attendance-today" label="Today's Attendance" value={stats.today_attendees || 0} icon={ScanLine} accent="bg-olive/20 text-olive" />
      </div>

      {/* Revenue chart + Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="bg-card_bg border border-border_soft p-6 rounded-xl lg:col-span-8">
          <h3 className="text-base font-semibold text-text_primary mb-6">Revenue Trend (6 months)</h3>
          <div className="h-72 w-full">
            {revenueTrend.length === 0 ? <div className="h-full flex items-center justify-center text-sm text-text_secondary">No data yet</div> : <RevenueChart data={revenueTrend} />}
          </div>
        </div>

        <div className="bg-card_bg border border-border_soft p-6 rounded-xl lg:col-span-4">
          <h3 className="text-base font-semibold text-text_primary mb-5 flex items-center gap-2"><Bell size={18} className="text-gold" /> Alerts</h3>
          <div className="space-y-5 max-h-72 overflow-y-auto custom-scrollbar pr-1">
            <AlertGroup title="Expiring Soon" color="text-accent_warning" icon={CalendarClock} items={alerts.expiring} render={(m) => <Link to="/admin/members" className="flex justify-between items-center text-sm hover:text-gold transition-colors"><span className="text-text_primary">{m.name}</span><span className="text-text_secondary text-xs">{m.expiry_date}</span></Link>} empty="No memberships expiring this week." />
            <AlertGroup title="Pending Dues" color="text-accent_danger" icon={Wallet} items={alerts.dues} render={(d) => <Link to="/admin/invoices" className="flex justify-between items-center text-sm hover:text-gold transition-colors"><span className="text-text_primary">{d.member}</span><span className="text-accent_danger text-xs font-semibold">{fmt(d.amount)}</span></Link>} empty="No pending dues." />
            <AlertGroup title="Follow-ups Due" color="text-gold" icon={Bell} items={alerts.followups} render={(f) => <Link to="/admin/leads" className="flex justify-between items-center text-sm hover:text-gold transition-colors"><span className="text-text_primary truncate mr-2">{f.name}</span><span className="text-text_secondary text-xs shrink-0">{f.date}</span></Link>} empty="No pending follow-ups." />
          </div>
        </div>
      </div>

      {/* Attendance + Packages */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card_bg border border-border_soft p-6 rounded-xl">
          <h3 className="text-base font-semibold text-text_primary mb-6">Attendance (last 7 days)</h3>
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={attendanceTrend} margin={{ top: 0, right: 0, left: -20, bottom: 0 }} barSize={26}>
                <CartesianGrid strokeDasharray="3 3" stroke="#243047" vertical={false} />
                <XAxis dataKey="name" stroke="#9CA3AF" fontSize={11} tickLine={false} axisLine={false} dy={8} />
                <YAxis stroke="#9CA3AF" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip cursor={{ fill: "#1B2435" }} contentStyle={chartTooltip} />
                <Bar dataKey="count" fill="#60A5FA" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-card_bg border border-border_soft p-6 rounded-xl">
          <h3 className="text-base font-semibold text-text_primary mb-6">Members by Package</h3>
          <div className="h-64 w-full">
            {packageData.length === 0 ? <div className="h-full flex items-center justify-center text-sm text-text_secondary">No members yet</div> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={packageData} layout="vertical" margin={{ top: 0, right: 10, left: 10, bottom: 0 }} barSize={20}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#243047" horizontal={false} />
                  <XAxis type="number" stroke="#9CA3AF" fontSize={11} hide allowDecimals={false} />
                  <YAxis dataKey="name" type="category" stroke="#9CA3AF" fontSize={12} width={110} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: "#1B2435" }} contentStyle={chartTooltip} />
                  <Bar dataKey="users" fill="#FACC15" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const AlertGroup = ({ title, color, icon: Icon, items, render, empty }) => (
  <div>
    <div className={`flex items-center gap-2 text-xs font-bold uppercase tracking-wider mb-2.5 ${color}`}>
      <Icon size={14} /> {title} {items.length > 0 && <span className="ml-auto bg-sidebar_hover text-text_secondary px-2 py-0.5 rounded-full text-[10px]">{items.length}</span>}
    </div>
    <div className="space-y-2">
      {items.length === 0 ? <p className="text-xs text-text_secondary/70 italic">{empty}</p> : items.map((it, i) => <div key={i}>{render(it)}</div>)}
    </div>
  </div>
);
